/**
 * Utility functions for consistent phone number formatting across the application
 * Format: +1 (XXX) XXX-XXXX (US phone numbers with country code)
 */

/**
 * Country codes supported for phone numbers
 * Sorted by code numerically for easy scanning
 */
export const COUNTRY_CODES = [
  { code: '+1', country: 'United States/Canada', digitCount: 10 },
  { code: '+7', country: 'Russia/Kazakhstan', digitCount: 10 },
  { code: '+20', country: 'Egypt', digitCount: 10 },
  { code: '+27', country: 'South Africa', digitCount: 9 },
  { code: '+30', country: 'Greece', digitCount: 10 },
  { code: '+31', country: 'Netherlands', digitCount: 9 },
  { code: '+32', country: 'Belgium', digitCount: 9 },
  { code: '+33', country: 'France', digitCount: 9 },
  { code: '+34', country: 'Spain', digitCount: 9 },
  { code: '+36', country: 'Hungary', digitCount: 9 },
  { code: '+39', country: 'Italy', digitCount: 10 },
  { code: '+40', country: 'Romania', digitCount: 9 },
  { code: '+41', country: 'Switzerland', digitCount: 9 },
  { code: '+43', country: 'Austria', digitCount: 10 },
  { code: '+44', country: 'United Kingdom', digitCount: 10 },
  { code: '+45', country: 'Denmark', digitCount: 8 },
  { code: '+46', country: 'Sweden', digitCount: 9 },
  { code: '+47', country: 'Norway', digitCount: 8 },
  { code: '+48', country: 'Poland', digitCount: 9 },
  { code: '+49', country: 'Germany', digitCount: 10 },
  { code: '+51', country: 'Peru', digitCount: 9 },
  { code: '+52', country: 'Mexico', digitCount: 10 },
  { code: '+53', country: 'Cuba', digitCount: 8 },
  { code: '+54', country: 'Argentina', digitCount: 10 },
  { code: '+55', country: 'Brazil', digitCount: 11 },
  { code: '+56', country: 'Chile', digitCount: 9 },
  { code: '+57', country: 'Colombia', digitCount: 10 },
  { code: '+58', country: 'Venezuela', digitCount: 10 },
  { code: '+60', country: 'Malaysia', digitCount: 9 },
  { code: '+61', country: 'Australia', digitCount: 9 },
  { code: '+62', country: 'Indonesia', digitCount: 10 },
  { code: '+63', country: 'Philippines', digitCount: 10 },
  { code: '+64', country: 'New Zealand', digitCount: 9 },
  { code: '+65', country: 'Singapore', digitCount: 8 },
  { code: '+66', country: 'Thailand', digitCount: 9 },
  { code: '+81', country: 'Japan', digitCount: 10 },
  { code: '+82', country: 'South Korea', digitCount: 10 },
  { code: '+84', country: 'Vietnam', digitCount: 9 },
  { code: '+86', country: 'China', digitCount: 11 },
  { code: '+90', country: 'Turkey', digitCount: 10 },
  { code: '+91', country: 'India', digitCount: 10 },
  { code: '+92', country: 'Pakistan', digitCount: 10 },
  { code: '+93', country: 'Afghanistan', digitCount: 9 },
  { code: '+94', country: 'Sri Lanka', digitCount: 9 },
  { code: '+95', country: 'Myanmar', digitCount: 9 },
  { code: '+98', country: 'Iran', digitCount: 10 },
  { code: '+212', country: 'Morocco', digitCount: 9 },
  { code: '+213', country: 'Algeria', digitCount: 9 },
  { code: '+216', country: 'Tunisia', digitCount: 8 },
  { code: '+218', country: 'Libya', digitCount: 9 },
  { code: '+220', country: 'Gambia', digitCount: 7 },
  { code: '+221', country: 'Senegal', digitCount: 9 },
  { code: '+222', country: 'Mauritania', digitCount: 8 },
  { code: '+223', country: 'Mali', digitCount: 8 },
  { code: '+224', country: 'Guinea', digitCount: 9 },
  { code: '+225', country: 'Ivory Coast', digitCount: 8 },
  { code: '+226', country: 'Burkina Faso', digitCount: 8 },
  { code: '+227', country: 'Niger', digitCount: 8 },
  { code: '+228', country: 'Togo', digitCount: 8 },
  { code: '+229', country: 'Benin', digitCount: 8 },
  { code: '+230', country: 'Mauritius', digitCount: 7 },
  { code: '+231', country: 'Liberia', digitCount: 8 },
  { code: '+232', country: 'Sierra Leone', digitCount: 8 },
  { code: '+233', country: 'Ghana', digitCount: 9 },
  { code: '+234', country: 'Nigeria', digitCount: 10 },
  { code: '+235', country: 'Chad', digitCount: 8 },
  { code: '+236', country: 'Central African Republic', digitCount: 8 },
  { code: '+237', country: 'Cameroon', digitCount: 9 },
  { code: '+238', country: 'Cape Verde', digitCount: 7 },
  { code: '+239', country: 'São Tomé and Príncipe', digitCount: 7 },
  { code: '+240', country: 'Equatorial Guinea', digitCount: 9 },
  { code: '+241', country: 'Gabon', digitCount: 7 },
  { code: '+242', country: 'Republic of the Congo', digitCount: 9 },
  { code: '+243', country: 'Democratic Republic of the Congo', digitCount: 9 },
  { code: '+244', country: 'Angola', digitCount: 9 },
  { code: '+245', country: 'Guinea-Bissau', digitCount: 7 },
  { code: '+246', country: 'British Indian Ocean Territory', digitCount: 7 },
  { code: '+248', country: 'Seychelles', digitCount: 7 },
  { code: '+249', country: 'Sudan', digitCount: 9 },
  { code: '+250', country: 'Rwanda', digitCount: 9 },
  { code: '+251', country: 'Ethiopia', digitCount: 9 },
  { code: '+252', country: 'Somalia', digitCount: 8 },
  { code: '+253', country: 'Djibouti', digitCount: 8 },
  { code: '+254', country: 'Kenya', digitCount: 9 },
  { code: '+255', country: 'Tanzania', digitCount: 9 },
  { code: '+256', country: 'Uganda', digitCount: 9 },
  { code: '+257', country: 'Burundi', digitCount: 8 },
  { code: '+258', country: 'Mozambique', digitCount: 9 },
  { code: '+260', country: 'Zambia', digitCount: 9 },
  { code: '+261', country: 'Madagascar', digitCount: 9 },
  { code: '+262', country: 'Réunion/Mayotte', digitCount: 9 },
  { code: '+263', country: 'Zimbabwe', digitCount: 9 },
  { code: '+264', country: 'Namibia', digitCount: 9 },
  { code: '+265', country: 'Malawi', digitCount: 9 },
  { code: '+266', country: 'Lesotho', digitCount: 8 },
  { code: '+267', country: 'Botswana', digitCount: 8 },
  { code: '+268', country: 'Eswatini', digitCount: 8 },
  { code: '+269', country: 'Comoros', digitCount: 7 },
  { code: '+290', country: 'Saint Helena', digitCount: 4 },
  { code: '+291', country: 'Eritrea', digitCount: 7 },
  { code: '+297', country: 'Aruba', digitCount: 7 },
  { code: '+298', country: 'Faroe Islands', digitCount: 6 },
  { code: '+299', country: 'Greenland', digitCount: 6 },
  { code: '+350', country: 'Gibraltar', digitCount: 8 },
  { code: '+351', country: 'Portugal', digitCount: 9 },
  { code: '+352', country: 'Luxembourg', digitCount: 9 },
  { code: '+353', country: 'Ireland', digitCount: 9 },
  { code: '+354', country: 'Iceland', digitCount: 7 },
  { code: '+355', country: 'Albania', digitCount: 9 },
  { code: '+356', country: 'Malta', digitCount: 8 },
  { code: '+357', country: 'Cyprus', digitCount: 8 },
  { code: '+358', country: 'Finland', digitCount: 9 },
  { code: '+359', country: 'Bulgaria', digitCount: 9 },
  { code: '+370', country: 'Lithuania', digitCount: 8 },
  { code: '+371', country: 'Latvia', digitCount: 8 },
  { code: '+372', country: 'Estonia', digitCount: 8 },
  { code: '+373', country: 'Moldova', digitCount: 8 },
  { code: '+374', country: 'Armenia', digitCount: 8 },
  { code: '+375', country: 'Belarus', digitCount: 9 },
  { code: '+376', country: 'Andorra', digitCount: 6 },
  { code: '+377', country: 'Monaco', digitCount: 8 },
  { code: '+378', country: 'San Marino', digitCount: 10 },
  { code: '+380', country: 'Ukraine', digitCount: 9 },
  { code: '+381', country: 'Serbia', digitCount: 9 },
  { code: '+382', country: 'Montenegro', digitCount: 8 },
  { code: '+383', country: 'Kosovo', digitCount: 8 },
  { code: '+385', country: 'Croatia', digitCount: 9 },
  { code: '+386', country: 'Slovenia', digitCount: 8 },
  { code: '+387', country: 'Bosnia and Herzegovina', digitCount: 8 },
  { code: '+389', country: 'North Macedonia', digitCount: 8 },
  { code: '+420', country: 'Czech Republic', digitCount: 9 },
  { code: '+421', country: 'Slovakia', digitCount: 9 },
  { code: '+423', country: 'Liechtenstein', digitCount: 7 },
  { code: '+500', country: 'Falkland Islands', digitCount: 5 },
  { code: '+501', country: 'Belize', digitCount: 7 },
  { code: '+502', country: 'Guatemala', digitCount: 8 },
  { code: '+503', country: 'El Salvador', digitCount: 8 },
  { code: '+504', country: 'Honduras', digitCount: 8 },
  { code: '+505', country: 'Nicaragua', digitCount: 8 },
  { code: '+506', country: 'Costa Rica', digitCount: 8 },
  { code: '+507', country: 'Panama', digitCount: 8 },
  { code: '+508', country: 'Saint Pierre and Miquelon', digitCount: 6 },
  { code: '+509', country: 'Haiti', digitCount: 8 },
  { code: '+590', country: 'Guadeloupe', digitCount: 9 },
  { code: '+591', country: 'Bolivia', digitCount: 8 },
  { code: '+592', country: 'Guyana', digitCount: 7 },
  { code: '+593', country: 'Ecuador', digitCount: 9 },
  { code: '+594', country: 'French Guiana', digitCount: 9 },
  { code: '+595', country: 'Paraguay', digitCount: 9 },
  { code: '+596', country: 'Martinique', digitCount: 9 },
  { code: '+597', country: 'Suriname', digitCount: 7 },
  { code: '+598', country: 'Uruguay', digitCount: 8 },
  { code: '+599', country: 'Netherlands Antilles', digitCount: 7 },
  { code: '+670', country: 'East Timor', digitCount: 8 },
  { code: '+672', country: 'Norfolk Island', digitCount: 6 },
  { code: '+673', country: 'Brunei', digitCount: 7 },
  { code: '+674', country: 'Nauru', digitCount: 7 },
  { code: '+675', country: 'Papua New Guinea', digitCount: 8 },
  { code: '+676', country: 'Tonga', digitCount: 5 },
  { code: '+677', country: 'Solomon Islands', digitCount: 7 },
  { code: '+678', country: 'Vanuatu', digitCount: 7 },
  { code: '+679', country: 'Fiji', digitCount: 7 },
  { code: '+680', country: 'Palau', digitCount: 7 },
  { code: '+681', country: 'Wallis and Futuna', digitCount: 6 },
  { code: '+682', country: 'Cook Islands', digitCount: 5 },
  { code: '+683', country: 'Niue', digitCount: 4 },
  { code: '+685', country: 'Samoa', digitCount: 7 },
  { code: '+686', country: 'Kiribati', digitCount: 5 },
  { code: '+687', country: 'New Caledonia', digitCount: 6 },
  { code: '+688', country: 'Tuvalu', digitCount: 5 },
  { code: '+689', country: 'French Polynesia', digitCount: 8 },
  { code: '+690', country: 'Tokelau', digitCount: 4 },
  { code: '+691', country: 'Micronesia', digitCount: 7 },
  { code: '+692', country: 'Marshall Islands', digitCount: 7 },
  { code: '+850', country: 'North Korea', digitCount: 10 },
  { code: '+852', country: 'Hong Kong', digitCount: 8 },
  { code: '+853', country: 'Macau', digitCount: 8 },
  { code: '+855', country: 'Cambodia', digitCount: 9 },
  { code: '+856', country: 'Laos', digitCount: 9 },
  { code: '+880', country: 'Bangladesh', digitCount: 10 },
  { code: '+886', country: 'Taiwan', digitCount: 9 },
  { code: '+960', country: 'Maldives', digitCount: 7 },
  { code: '+961', country: 'Lebanon', digitCount: 8 },
  { code: '+962', country: 'Jordan', digitCount: 9 },
  { code: '+963', country: 'Syria', digitCount: 9 },
  { code: '+964', country: 'Iraq', digitCount: 10 },
  { code: '+965', country: 'Kuwait', digitCount: 8 },
  { code: '+966', country: 'Saudi Arabia', digitCount: 9 },
  { code: '+967', country: 'Yemen', digitCount: 9 },
  { code: '+968', country: 'Oman', digitCount: 8 },
  { code: '+970', country: 'Palestine', digitCount: 9 },
  { code: '+971', country: 'United Arab Emirates', digitCount: 9 },
  { code: '+972', country: 'Israel', digitCount: 9 },
  { code: '+973', country: 'Bahrain', digitCount: 8 },
  { code: '+974', country: 'Qatar', digitCount: 8 },
  { code: '+975', country: 'Bhutan', digitCount: 8 },
  { code: '+976', country: 'Mongolia', digitCount: 8 },
  { code: '+977', country: 'Nepal', digitCount: 10 },
  { code: '+992', country: 'Tajikistan', digitCount: 9 },
  { code: '+993', country: 'Turkmenistan', digitCount: 8 },
  { code: '+994', country: 'Azerbaijan', digitCount: 9 },
  { code: '+995', country: 'Georgia', digitCount: 9 },
  { code: '+996', country: 'Kyrgyzstan', digitCount: 9 },
  { code: '+998', country: 'Uzbekistan', digitCount: 9 }
];

/**
 * Format only the 10-digit phone number (without country code)
 * @param value - Raw digits string (should already be digits only)
 * @returns Formatted phone number: (555) 123-4567
 *
 * Note: Returns empty string if no digits, allowing user to clear the field
 * This function expects raw digits as input (no formatting characters)
 */
export const formatPhoneNumberOnly = (value: string): string => {
  // Ensure we only have digits (defensive)
  const digits = value.replace(/\D/g, '');

  // Allow clearing the field - return empty if no digits
  if (digits.length === 0) return '';

  // Format progressively based on number of digits
  if (digits.length <= 3) {
    // (555
    return `(${digits}`;
  } else if (digits.length <= 6) {
    // (555) 123
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else {
    // (555) 123-4567
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
};

/**
 * Format a phone number with US country code +1
 * @param value - Raw phone number string (can contain any characters)
 * @returns Formatted phone number: +1 (555) 123-4567
 *
 * Note: Returns empty string if no digits, allowing user to clear the field
 * DEPRECATED: Use formatPhoneNumberOnly with separate country code dropdown
 */
export const formatPhoneNumber = (value: string): string => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  // Allow clearing the field - return empty if no digits
  if (digits.length === 0) return '';

  // Format progressively as user types
  // Only show area code section when user has entered at least one digit
  if (digits.length <= 3) {
    return `+1 (${digits}`;
  } else if (digits.length <= 6) {
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else {
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
};

/**
 * Validate if a phone number is complete (10 digits)
 * @param value - Phone number string (formatted or unformatted)
 * @returns true if phone has exactly 10 digits
 */
export const isValidPhoneNumber = (value: string): boolean => {
  const digits = value.replace(/\D/g, '');
  return digits.length === 10;
};

/**
 * Maximum length for formatted phone number input (without country code)
 * (555) 123-4567 = 14 characters
 */
export const PHONE_MAX_LENGTH = 14;

/**
 * Maximum length for full formatted phone with country code
 * +1 (555) 123-4567 = 17 characters
 */
export const PHONE_MAX_LENGTH_WITH_COUNTRY = 17;

/**
 * Placeholder text for phone number inputs (without country code)
 */
export const PHONE_PLACEHOLDER = '(555) 123-4567';

/**
 * Parse existing phone number to extract country code and number
 * @param fullPhone - Full phone string like "+15551234567" or "5551234567"
 * @returns Object with countryCode and phoneNumber
 */
export const parsePhoneNumber = (fullPhone: string): { countryCode: string; phoneNumber: string } => {
  console.log('[parsePhoneNumber] Input:', fullPhone);
  
  if (!fullPhone) {
    console.log('[parsePhoneNumber] Empty input, returning defaults');
    return { countryCode: '+1', phoneNumber: '' };
  }

  const digits = fullPhone.replace(/\D/g, '');
  console.log('[parsePhoneNumber] Extracted digits:', digits, 'Length:', digits.length);

  // If starts with 1 and has 11 digits, it's +1 country code (e.g., "+15551234567" -> "15551234567")
  if (digits.length === 11 && digits.startsWith('1')) {
    const result = {
      countryCode: '+1',
      phoneNumber: digits.slice(1) // Remove the leading 1
    };
    console.log('[parsePhoneNumber] Matched 11-digit +1 format:', result);
    return result;
  }

  // If 10 digits, assume US/Canada (+1)
  if (digits.length === 10) {
    const result = {
      countryCode: '+1',
      phoneNumber: digits
    };
    console.log('[parsePhoneNumber] Matched 10-digit format:', result);
    return result;
  }

  // If 12+ digits starting with 1, it's likely +1 with a 10-digit number
  if (digits.length >= 11 && digits.startsWith('1')) {
    const result = {
      countryCode: '+1',
      phoneNumber: digits.slice(1, 11) // Take 10 digits after the country code
    };
    console.log('[parsePhoneNumber] Matched 11+ digit +1 format:', result);
    return result;
  }

  // Default: take up to first 10 digits
  // This handles cases where digits might be incomplete or malformed
  const phoneDigits = digits.slice(0, 10);
  const result = {
    countryCode: '+1',
    phoneNumber: phoneDigits
  };
  console.log('[parsePhoneNumber] Default fallback:', result);
  return result;
};
